import clsx from 'clsx'
import {KTSVG, toAbsoluteUrl} from '../../../../../_gori/helpers'
import {CHATBOT_MODE, MESSAGE_TYPE} from '../../definitions'
import {useCallback, useEffect, useState} from 'react'
import {useChatbotContext} from '../../context'
import {useIntl} from 'react-intl'
import ChatbotService from '../../services/request'
import {first} from 'lodash'
import {MessageType} from '../../definitions'
import {formatTimeAgo} from '../../utils'
import {TIME_ONE_MINUTE} from '../../definitions/time'
import {LANGUAGE_KEY} from '../../../../../_gori/config/LanguageConfig'

type ChatbotHeaderProps = {
  onHide: () => void
  onRefresh: (withLoading?: boolean) => void
  loading: boolean
}

export const ChatbotHeader = ({onHide, onRefresh, loading}: ChatbotHeaderProps) => {
  const intl = useIntl()

  const {mode, setMode, messages} = useChatbotContext()
  const [timeAgo, setTimeAgo] = useState<string | null>(null)
  // const [showSetting, setShowSetting] = useState<boolean>(false)
  // const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false)
  // const [loadingDelete, setLoadingDelete] = useState<boolean>(false)
  // const target = useRef(null)

  const onChangeMode = useCallback(() => {
    if (mode === CHATBOT_MODE.DEFAULT) {
      setMode(CHATBOT_MODE.LARGE)
      return
    }
    if (mode === CHATBOT_MODE.LARGE) {
      setMode(CHATBOT_MODE.DEFAULT)
      return
    }
    setMode(CHATBOT_MODE.DEFAULT)
  }, [mode, setMode])

  const onClearChatHistory = async () => {
    await ChatbotService.clearChatHistory()
    onRefresh(false)

    /* NOTE: [N] hide clear history for demo */
    // try {
    //   setLoadingDelete(true)
    //   await ChatbotService.clearChatHistory()
    //   toast.success(intl.formatMessage({id: 'CLEAR_CHAT_HISTORY_SUCCESS'}))
    //   setShowConfirmModal(false)
    //   onRefresh()
    // } catch (error) {
    //   toast.error(intl.formatMessage({id: 'SOMETHING_WENT_WRONG'}))
    // } finally {
    //   setLoadingDelete(false)
    // }
  }

  const isSurveyMessage = useCallback((type?: MessageType) => {
    return type === MESSAGE_TYPE.SURVEY
  }, [])

  useEffect(() => {
    if (!messages.length) {
      setTimeAgo('')
      return
    }

    const responseMessages = messages.filter((msg) => !isSurveyMessage(msg.type))
    const firstResponseMessage = first(responseMessages)
    if (!firstResponseMessage) {
      setTimeAgo('')
      return
    }

    const countLastTimeSent = async () => {
      const diff =
        new Date().getTime() -
        new Date(firstResponseMessage.created_at.replace(' ', 'T') + 'Z').getTime()

      if (diff >= TIME_ONE_MINUTE) {
        setTimeAgo(
          diff >= TIME_ONE_MINUTE ? formatTimeAgo(firstResponseMessage.created_at, intl) : ''
        )
      } else {
        setTimeAgo('')
      }
    }

    const intervalId = setInterval(countLastTimeSent, TIME_ONE_MINUTE)
    countLastTimeSent()

    return () => clearInterval(intervalId)
  }, [messages, intl, isSurveyMessage])

  return (
    <div className='d-flex text-white px-4 py-3 bg-primary-100 align-items-center justify-content-between'>
      <div className='d-flex align-items-center gap-2'>
        <div className='w-50px h-50px rounded-circle bg-light-primary d-flex justify-content-center align-items-center'>
          <img alt='avatar' src={toAbsoluteUrl(`/media/gori/chatbot/avatar.svg`)} />
        </div>
        <div>
          <strong className='fs-6'>Bae</strong>
          <div className='fs-8 fw-light text-white'>
            {timeAgo
              ? intl.locale === LANGUAGE_KEY.ZH
                ? `${timeAgo} ${intl.formatMessage({id: 'LAST_UPDATED'})}`
                : `${intl.formatMessage({id: 'LAST_UPDATED'})} ${timeAgo}`
              : intl.formatMessage({id: 'JUST_NOW'})}
          </div>
        </div>
      </div>
      <div className='d-flex gap-4'>
        <div className='w-30px' onClick={onClearChatHistory}></div>
        <div
          className={'btn-icon text-hover-primary cursor-pointer mb-2'}
          onClick={() => onRefresh(true)}
        >
          <KTSVG
            path='/media/gori/chatbot/refresh.svg'
            className={clsx('svg-icon svg-icon-4')}
            svgClassName={clsx({spin_reverse: loading})}
          />
        </div>
        <div className={'btn-icon text-hover-primary cursor-pointer mb-2'} onClick={onChangeMode}>
          <KTSVG
            path={`/media/gori/chatbot/${
              mode === CHATBOT_MODE.DEFAULT ? 'maximize' : 'minimize'
            }.svg`}
            className={clsx('svg-icon svg-icon-4')}
          />
        </div>
        <div className={'btn-icon text-hover-primary cursor-pointer mb-2'} onClick={onHide}>
          <KTSVG path='/media/gori/chatbot/collapse.svg' className={clsx('svg-icon svg-icon-4')} />
        </div>
        {/* NOTE: [N] hide clear history for demo */}
        {/* <div
          className={'btn-icon text-hover-primary cursor-pointer mb-2'}
          onClick={() => setShowSetting((prev) => !prev)}
          ref={target}
        >
          <KTSVG path={`/media/gori/chatbot/setting.svg`} className={clsx('svg-icon svg-icon-4')} />
        </div>
        <ConfirmActionModal
          show={showConfirmModal}
          classNameDialog='modal-size-lg'
          title={intl.formatMessage({id: 'CLEAR_CHAT_HISTORY'})}
          message={
            <span className='fw-bold text-center'>
              <p>{intl.formatMessage({id: 'ARE_YOU_SURE_YOU_WANT_TO_PROCEED'})}</p>
              <p>
                {intl.formatMessage({
                  id: 'THIS_ACTION_CANNOT_BE_UNDONE_PLEASE_REVIEW_YOUR_DECISION_CAREFULLY_BEFORE_PROCEEDING',
                })}
              </p>
            </span>
          }
          messagePrimary={intl.formatMessage({id: 'CONFIRM'})}
          messageSecondary={intl.formatMessage({id: 'CANCEL'})}
          handlePrimaryAction={onClearChatHistory}
          handleSecondaryAction={() => setShowConfirmModal(false)}
          handleClose={() => setShowConfirmModal(false)}
          loadingPrimary={loadingDelete}
        />
        <Overlay
          show={showSetting}
          target={target.current}
          placement='bottom'
          containerPadding={20}
          rootClose
          onHide={() => setShowSetting(false)}
        >
          <Popover id='chatbot-setting-popover' className={'shadow-lg rounded-2 overflow-hidden'}>
            <Popover.Body>
              <div
                className='d-flex flex-column gap-4'
                onClick={() => {
                  setShowSetting(false)
                  setShowConfirmModal(true)
                }}
              >
                <div className='cursor-pointer text-hover-primary'>
                  {intl.formatMessage({id: 'CLEAR_CHAT_HISTORY'})}
                </div>
              </div>
            </Popover.Body>
          </Popover>
        </Overlay> */}
      </div>
    </div>
  )
}
